import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsApi } from '@/services/api';
import { ProductCard } from '@/features/products/ProductCard';
import { SearchBar } from '@/components/shared/SearchBar';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavCategories } from '@/context/NavCategoriesContext';
import { trackCategoryVisit } from '@/utils/behaviorTracking';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { SITE_NAME, truncateMeta } from '@/utils/seo';
import { scheduleReleaseUiScrollLock } from '@/utils/releaseUiScrollLock';

export const ProductsPage = () => {
  const { categories: navCategories, getSubcategoriesForParent, findSubcategoryLabel } = useNavCategories();
  const mainCategoryOptions = navCategories.map((c) => ({ id: c.id, name: c.name, slug: c.slug }));
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = searchParams.get('category') || '';
  const resolvedCategoryId = useMemo(() => {
    if (!category) return '';
    const m = navCategories.find((c) => c.id === category || c.slug === category);
    return m ? m.id : category;
  }, [category, navCategories]);
  const subcategorySlug = searchParams.get('subcategory') || '';
  const search = searchParams.get('search') || '';
  const county = searchParams.get('county') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';

  const [localMinPrice, setLocalMinPrice] = useState(minPrice);
  const [localMaxPrice, setLocalMaxPrice] = useState(maxPrice);

  useEffect(() => {
    if (!category || category === 'all') return;
    const match = navCategories.find((c) => c.id === category || c.slug === category);
    if (match && match.id !== category) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('category', match.id);
          return p;
        },
        { replace: true },
      );
    }
  }, [category, navCategories, setSearchParams]);

  useEffect(() => {
    if (resolvedCategoryId && resolvedCategoryId !== 'all') {
      trackCategoryVisit(resolvedCategoryId);
    }
  }, [resolvedCategoryId]);

  useEffect(
    () => () => {
      scheduleReleaseUiScrollLock();
    },
    [],
  );

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (resolvedCategoryId && resolvedCategoryId !== 'all') {
          params.set('category', resolvedCategoryId);
        }
        if (subcategorySlug && subcategorySlug !== 'all') params.set('subcategory', subcategorySlug);
        if (search) params.set('search', search);
        if (county && county !== 'all') params.set('county', county);
        if (sort) params.set('sort', sort);
        if (minPrice) params.set('min_price', minPrice);
        if (maxPrice) params.set('max_price', maxPrice);
        params.set('limit', '12');
        params.set('skip', String((page - 1) * 12));

        const response = await productsApi.getAll(Object.fromEntries(params));
        setProducts(response.data.products);
        setTotal(response.data.total);
        setPages(response.data.pages);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [resolvedCategoryId, subcategorySlug, search, county, sort, page, minPrice, maxPrice]);

  const handleSortChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', value);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleCategoryChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', value);
    }
    newParams.delete('subcategory');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleSubcategoryChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('subcategory');
    } else {
      newParams.set('subcategory', value);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleApplyPriceFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    if (localMinPrice) {
      newParams.set('min_price', localMinPrice);
    } else {
      newParams.delete('min_price');
    }
    if (localMaxPrice) {
      newParams.set('max_price', localMaxPrice);
    } else {
      newParams.delete('max_price');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
    setFiltersOpen(false);
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryName = () => {
    const cat = mainCategoryOptions.find((c) => c.id === resolvedCategoryId);
    return cat ? cat.name : 'Alla produkter';
  };

  const getHeading = () => {
    if (!resolvedCategoryId) return 'Alla produkter';
    const main = getCategoryName();
    if (subcategorySlug) {
      return `${main} · ${findSubcategoryLabel(resolvedCategoryId, subcategorySlug)}`;
    }
    return main;
  };

  const breadcrumbs = [
    { label: 'Produkter', href: '/produkter' },
    ...(resolvedCategoryId
      ? [{ label: getCategoryName(), href: `/produkter?category=${resolvedCategoryId}` }]
      : []),
    ...(resolvedCategoryId && subcategorySlug
      ? [{ label: findSubcategoryLabel(resolvedCategoryId, subcategorySlug) }]
      : []),
  ];

  const canonicalPath = useMemo(() => {
    const qs = searchParams.toString();
    return qs ? `/produkter?${qs}` : '/produkter';
  }, [searchParams]);

  const headingText = getHeading();
  let pageTitle = headingText;
  if (search.trim()) {
    pageTitle = `Sök: ${truncateMeta(search.trim(), 52)}`;
  } else if (county && county !== 'all') {
    pageTitle = `${headingText} — ${county}`;
  }
  const intro =
    headingText === 'Alla produkter'
      ? 'Utforska byggmaterial, verktyg och tillbehör'
      : `Köp ${headingText.replace(/ · /g, ', ')}`;
  const pageDescription = truncateMeta(
    `${intro} på ${SITE_NAME}. Just nu ${total} produkter från godkända svenska återförsäljare.`,
  );

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="products-page">
      <SeoMeta
        title={pageTitle}
        description={pageDescription}
        canonicalPath={canonicalPath}
      />
      <div className="container-main py-8">
        <Breadcrumbs items={breadcrumbs} />

        {/* Search Bar */}
        <div className="mb-8">
          <SearchBar 
            initialSearch={search} 
            initialCategory={resolvedCategoryId || category} 
            initialCounty={county}
          />
        </div>

        {/* Filters & Sorting */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-xl md:text-2xl font-black text-[#0F172A]">
              {getHeading()}
            </h1>
            <span className="text-gray-500 text-sm">({total} produkter)</span>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Mobile Filters */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="sm:hidden" data-testid="filters-btn">
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[60vh]">
                <SheetHeader>
                  <SheetTitle>Filtrera produkter</SheetTitle>
                </SheetHeader>
                <div className="py-6 space-y-6">
                  <div>
                    <Label>Kategori</Label>
                    <Select value={resolvedCategoryId || 'all'} onValueChange={handleCategoryChange}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Välj kategori" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alla kategorier</SelectItem>
                        {mainCategoryOptions.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {resolvedCategoryId && getSubcategoriesForParent(resolvedCategoryId).length > 0 && (
                    <div>
                      <Label>Underkategori</Label>
                      <Select value={subcategorySlug || 'all'} onValueChange={handleSubcategoryChange}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Alla underkategorier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alla underkategorier</SelectItem>
                          {getSubcategoriesForParent(resolvedCategoryId).map((sub) => (
                            <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Prisintervall</Label>
                    <div className="flex gap-4 mt-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={localMinPrice}
                        onChange={(e) => setLocalMinPrice(e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={localMaxPrice}
                        onChange={(e) => setLocalMaxPrice(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button onClick={handleApplyPriceFilter} className="w-full bg-[#FF8A00] hover:bg-[#e67c00]">
                    Tillämpa filter
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            {/* Desktop Category Filter */}
            <div className="hidden sm:flex flex-wrap items-center gap-2">
              <Select value={resolvedCategoryId || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="Kategori" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla kategorier</SelectItem>
                  {mainCategoryOptions.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {resolvedCategoryId && getSubcategoriesForParent(resolvedCategoryId).length > 0 && (
                <Select value={subcategorySlug || 'all'} onValueChange={handleSubcategoryChange}>
                  <SelectTrigger className="w-[200px]" data-testid="subcategory-filter">
                    <SelectValue placeholder="Underkategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alla underkategorier</SelectItem>
                    {getSubcategoriesForParent(resolvedCategoryId).map((sub) => (
                      <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Sorting */}
            <Select value={sort} onValueChange={handleSortChange}>
              <SelectTrigger className="w-[160px]" data-testid="sort-select">
                <SelectValue placeholder="Sortering" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Nyast först</SelectItem>
                <SelectItem value="price_asc">Pris: Lägst</SelectItem>
                <SelectItem value="price_desc">Pris: Högst</SelectItem>
                <SelectItem value="popular">Mest populär</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white rounded-md aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-md">
            <p className="text-gray-500 text-lg">Inga produkter hittades med dina sökkriterier.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setSearchParams({})}
            >
              Rensa filter
            </Button>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-12" data-testid="pagination">
            <Button
              variant="outline"
              size="icon"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            {[...Array(Math.min(pages, 5))].map((_, i) => {
              let pageNum;
              if (pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= pages - 2) {
                pageNum = pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => handlePageChange(pageNum)}
                  className={page === pageNum ? 'bg-[#003B73]' : ''}
                >
                  {pageNum}
                </Button>
              );
            })}
            
            <Button
              variant="outline"
              size="icon"
              disabled={page >= pages}
              onClick={() => handlePageChange(page + 1)}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
